import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import * as THREE from 'three';
import confetti from 'canvas-confetti';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/* ─── i18n ──────────────────────────────────────────────────────────────── */
const LABELS = {
  en: {
    loading:        'Loading your results...',
    empty:          'Start your first session to see results here! 🚀',
    retry:          'Retry',
    sessions:       'Sessions',
    exams:          'Exams',
    toImprove:      'To Improve',
    xp:             'XP Earned',
    mastery:        'Mastery',
    recentSessions: 'Recent sessions — tap to view',
    examsTab:       'Exams 📝',
    improveTab:     'Areas to Improve 🎯',
    noExams:        'No exams yet — keep learning! 🚀',
    noImprove:      'Everything looks great! Keep it up 🌟',
    noData:         'No data available',
    sessionModalTitle: 'Session Detail',
    examModalTitle:    'Exam Detail',
    topicsCovered:     'Topics covered',
    strengths:         'Strengths in this session',
    areasToWork:       'Areas to work on',
    questionBreakdown: 'Question breakdown',
    topicBreakdown:    'Performance by topic',
    recommendations:   'Jahbath recommends',
    correct:           'Correct',
    wrong:             'Wrong',
    score:             'Score',
  },
  ar: {
    loading:        'جاري تحميل نتائجك...',
    empty:          'ابدأ أول جلسة لتظهر نتائجك هنا! 🚀',
    retry:          'إعادة المحاولة',
    sessions:       'جلسات',
    exams:          'اختبارات',
    toImprove:      'للتطوير',
    xp:             'نقاط',
    mastery:        'الإتقان',
    recentSessions: 'آخر الجلسات — اضغط للتفاصيل',
    examsTab:       'اختبارات 📝',
    improveTab:     'نقاط للتطوير 🎯',
    noExams:        'لا اختبارات بعد — واصل التعلم! 🚀',
    noImprove:      'ممتاز! استمر في التميز 🌟',
    noData:         'لا توجد بيانات',
    sessionModalTitle: 'تفاصيل الجلسة',
    examModalTitle:    'تفاصيل الاختبار',
    topicsCovered:     'المواضيع المغطاة',
    strengths:         'نقاط القوة في هذه الجلسة',
    areasToWork:       'نقاط للتطوير',
    questionBreakdown: 'تفاصيل الأسئلة',
    topicBreakdown:    'الأداء حسب الموضوع',
    recommendations:   'توصيات جهبذ',
    correct:           'صحيح',
    wrong:             'خطأ',
    score:             'النتيجة',
  },
};
const t = (lang, key) => LABELS[lang]?.[key] ?? LABELS.en[key];

/* ─── Helpers ───────────────────────────────────────────────────────────── */
const subjLang = (subj) => subj.delivery_language === 'ar' ? 'ar' : 'en';

const subjectEmoji = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('science') || n.includes('علوم'))                               return '🔬';
  if (n.includes('math') || n.includes('رياض'))                                  return '📐';
  if (n.includes('english') || n.includes('انجليز'))                             return '📖';
  if (n.includes('arabic') || n.includes('عربي') || n.includes('اللغة العربية')) return '📝';
  if (n.includes('history') || n.includes('تاريخ'))                              return '🏛️';
  if (n.includes('islamic') || n.includes('تربية') || n.includes('إسلام'))       return '🕌';
  if (n.includes('global') || n.includes('perspectives'))                        return '🌍';
  if (n.includes('physics') || n.includes('فيزياء'))                             return '⚡';
  if (n.includes('chemistry') || n.includes('كيمياء'))                           return '⚗️';
  if (n.includes('biology') || n.includes('أحياء'))                              return '🧬';
  return '📚';
};

const ringColor = (pct) => {
  if (pct >= 70) return { stroke: '#10B981', track: '#D1FAE5', text: '#059669' };
  if (pct >= 40) return { stroke: '#F59E0B', track: '#FEF3C7', text: '#D97706' };
  return { stroke: '#EF4444', track: '#FEE2E2', text: '#EF4444' };
};

const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

/* ─── Exam meta ─────────────────────────────────────────────────────────── */
const EXAM_META = {
  comprehension_check: { label:'فحص الفهم',    labelEn:'Comprehension', bg:'#F0FDF4', color:'#059669', icon:'🔍' },
  unit:                { label:'اختبار الوحدة', labelEn:'Unit Test',     bg:'#EFF6FF', color:'#1D4ED8', icon:'📘' },
  unit_test:           { label:'اختبار الوحدة', labelEn:'Unit Test',     bg:'#EFF6FF', color:'#1D4ED8', icon:'📘' },
  weekly:              { label:'أسبوعي',         labelEn:'Weekly',        bg:'#F5F3FF', color:'#7C3AED', icon:'📅' },
  monthly:             { label:'شهري',           labelEn:'Monthly',       bg:'#FFFBEB', color:'#D97706', icon:'🗓️' },
  comprehensive:       { label:'شامل',           labelEn:'Comprehensive', bg:'#FFF7ED', color:'#C2410C', icon:'📚' },
  mid_year:            { label:'نصف العام',       labelEn:'Mid-Year',      bg:'#F0F9FF', color:'#0369A1', icon:'📌' },
  end_year:            { label:'آخر العام',        labelEn:'End-Year',      bg:'#EEF2FF', color:'#4338CA', icon:'🏆' },
  challenge:           { label:'تحدي',            labelEn:'Challenge',     bg:'#FFF1F2', color:'#E11D48', icon:'⚡' },
  on_demand:           { label:'عند الطلب',       labelEn:'On Demand',     bg:'#F0FDFA', color:'#0D9488', icon:'🎯' },
  demand:              { label:'عند الطلب',       labelEn:'On Demand',     bg:'#F0FDFA', color:'#0D9488', icon:'🎯' },
};
const examMeta = (type) => EXAM_META[type] || { label: type, bg: '#F3F4F6', color: '#6B7280' };

/* ─── SVG Circle Ring ───────────────────────────────────────────────────── */
const CircleRing = ({ pct, size = 64, strokeWidth = 6, animate = false }) => {
  const r      = (size / 2) - (strokeWidth + 2);
  const circ   = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const { stroke, track, text } = ringColor(pct);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={stroke} strokeWidth={strokeWidth}
          strokeDasharray={circ}
          strokeDashoffset={animate ? circ : offset}
          strokeLinecap="round"
          style={animate ? {
            transition: 'stroke-dashoffset 1.4s cubic-bezier(0.34,1.56,0.64,1)',
            strokeDashoffset: offset,
          } : {}}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: size * 0.22, fontWeight: 800, color: text, lineHeight: 1 }}>
          {Math.round(pct)}%
        </span>
      </div>
    </div>
  );
};

/* ─── Skeleton card ─────────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div style={{
      background: 'white', borderRadius: 20, border: '1px solid #E5E7EB',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 14, padding: '28px 16px',
    }}>
      <div className="rg-shim" style={{ width: 52, height: 52, borderRadius: '50%' }} />
      <div className="rg-shim" style={{ width: '70%', height: 14, borderRadius: 8 }} />
      <div className="rg-shim" style={{ width: '45%', height: 10, borderRadius: 6 }} />
    </div>
  );
}

/* ─── Subject card ──────────────────────────────────────────────────────── */
function SubjectCard({ subj, selected, onClick }) {
  const pct                      = Math.round(subj.avg_correct_percentage || 0);
  const { stroke, track, text }  = ringColor(pct);
  const isQatar                  = subj.curriculum_type === 'qatar';
  const emoji                    = subjectEmoji(subj.subject_name);

  const accentGrad = pct >= 70
    ? 'linear-gradient(90deg,#059669,#10B981)'
    : pct >= 40
      ? 'linear-gradient(90deg,#D97706,#F59E0B)'
      : 'linear-gradient(90deg,#DC2626,#F87171)';

  const glowColor = pct >= 70 ? '#059669' : pct >= 40 ? '#D97706' : '#EF4444';

  // Fixed 7-bar pattern based on avg_correct_percentage only
  const filledCount = Math.ceil((pct / 100) * 7);
  const BAR_HEIGHTS = [18, 24, 14, 20, 16, 22, 12];
  const bars = BAR_HEIGHTS.map((h, i) => ({ height: h, filled: i < filledCount }));

  return (
    <div
      className={`rg-card${selected ? ' rg-selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        background: 'white', borderRadius: 24, border: '2px solid #E5E7EB',
        overflow: 'hidden', position: 'relative',
        fontFamily: "'Sora',sans-serif",
      }}
    >
      {/* Top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 4,
        borderRadius: '20px 20px 0 0', background: accentGrad,
      }} />

      {/* Glow blob */}
      <div style={{
        position: 'absolute', top: -30, right: -30,
        width: 90, height: 90, borderRadius: '50%',
        background: glowColor, opacity: 0.08, pointerEvents: 'none',
      }} />

      {/* Card content */}
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 6, padding: 20,
        textAlign: 'center', height: '100%', boxSizing: 'border-box',
      }}>

        {/* Emoji + level ring */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <span style={{ fontSize: 26 }}>{emoji}</span>
          <div style={{
            position: 'absolute', inset: -4, borderRadius: '50%',
            border: `2.5px ${pct >= 70 ? 'solid' : 'dashed'} ${stroke}`,
            pointerEvents: 'none',
          }} />
        </div>

        {/* Subject name */}
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1E3A5F', lineHeight: 1.3, marginBottom: 6 }}>
          {subj.subject_name}
        </div>

        {/* Hex bars */}
        <div style={{
          display: 'flex', gap: 3, justifyContent: 'center',
          alignItems: 'flex-end', height: 24,
        }}>
          {bars.map(({ height: h, filled }, i) => (
            <div
              key={i}
              className="rg-bar"
              style={{ width: 7, height: h, borderRadius: 2, background: filled ? stroke : track }}
            />
          ))}
        </div>

        {/* Percentage */}
        <div style={{ fontSize: 14, fontWeight: 800, color: text }}>
          {pct}%
        </div>

        {/* XP pill */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: 20, padding: '2px 7px',
          fontSize: 9, fontWeight: 700, color: '#D97706',
        }}>
          ⭐ {subj.total_xp} XP
        </div>

      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main export
═══════════════════════════════════════════════════════════════════════════ */
export default function Results() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loading,           setLoading]           = useState(true);
  const [error,             setError]             = useState(null);
  const [subjects,          setSubjects]          = useState([]);
  const [avatarUrl,         setAvatarUrl]         = useState(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState(null);
  const [activeTab,         setActiveTab]         = useState('exams');
  const [sessionModal,      setSessionModal]      = useState(null);
  const [examModal,         setExamModal]         = useState(null);
  const [modalLoading,      setModalLoading]      = useState(false);
  const [modalError,        setModalError]        = useState(null);
  const [quizDismissed,     setQuizDismissed]     = useState(false);
  const [showReview,        setShowReview]        = useState(false);
  const detailRef = useRef(null);
  const { lang: ctxLang } = useLanguage();

  const globalLang = (() => {
    if (location.state?.delivery_language) return location.state.delivery_language;
    try {
      const sd = JSON.parse(localStorage.getItem('student_data') || '{}');
      return sd.language || ctxLang;
    } catch { return 'ar'; }
  })();

  const quizState = !quizDismissed && location.state?.fromQuiz ? location.state : null;

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  const CanvasPortal = () =>
    typeof document !== 'undefined'
      ? createPortal(
          <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }} />,
          document.body
        )
      : null;

  /* ── Confetti on post-quiz mount ── */
  useEffect(() => {
    if (!location.state?.fromQuiz) return;
    if ((location.state?.score || 0) >= 70) {
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.5 } });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const studentId = (() => {
      try { return JSON.parse(localStorage.getItem('student_data') || '{}')?.id; } catch { return null; }
    })();
    const token = localStorage.getItem('student_token') || '';

    if (!studentId) { navigate('/student/auth', { replace: true }); return; }

    setLoading(true);
    setError(null);

    try {
      const [resultsRes, dashRes] = await Promise.all([
        studentFetch(`${API}/student/results?student_id=${studentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        studentFetch(`${API}/student/dashboard/${studentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (!resultsRes || !dashRes) return;

      const [resultsData, dashData] = await Promise.all([
        resultsRes.json(),
        dashRes.json(),
      ]);

      if (dashData?.student?.avatar_url) setAvatarUrl(dashData.student.avatar_url);

      if (!resultsRes.ok || !resultsData.success) {
        throw new Error(resultsData.error || 'Failed to load results.');
      }

      setSubjects(resultsData.subjects || []);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('student_token')}` });

  async function openSessionModal(sessionId) {
    if (!sessionId) return;
    setModalLoading(true);
    setModalError(null);
    setSessionModal('loading');
    setExamModal(null);
    try {
      const res  = await studentFetch(`${API}/student/session-detail/${sessionId}`, { headers: authHeader() });
      if (!res) return;
      const data = await res.json();
      if (data.success) setSessionModal(data.session);
      else { setSessionModal(null); setModalError(data.error || 'Failed to load'); }
    } catch { setSessionModal(null); setModalError('Network error'); }
    finally { setModalLoading(false); }
  }

  async function openExamModal(examId) {
    if (!examId) return;
    setModalLoading(true);
    setModalError(null);
    setExamModal('loading');
    setSessionModal(null);
    try {
      const res  = await studentFetch(`${API}/student/exam-detail/${examId}`, { headers: authHeader() });
      if (!res) return;
      const data = await res.json();
      if (data.success) setExamModal(data.exam);
      else { setExamModal(null); setModalError(data.error || 'Failed to load'); }
    } catch { setExamModal(null); setModalError('Network error'); }
    finally { setModalLoading(false); }
  }

  /* ─── Post-quiz view ────────────────────────────────────────────────────── */
  if (quizState) {
    const qs        = quizState;
    const lang      = globalLang;
    const qDir      = lang === 'ar' ? 'rtl' : 'ltr';
    const meta      = examMeta(qs.quiz_type);
    const timeMins  = Math.floor((qs.time_seconds || 0) / 60);
    const timeSecs  = String((qs.time_seconds || 0) % 60).padStart(2, '0');
    const timeStr   = `${timeMins}:${timeSecs}`;
    const typeLabel = lang === 'ar' ? meta.label : (meta.labelEn || meta.label);
    const gradeColor = qs.score >= 70 ? '#059669' : qs.score >= 60 ? '#D97706' : '#EF4444';

    const gradeLabel = qs.score >= 90
      ? (lang === 'ar' ? 'ممتاز'             : 'Excellent')
      : qs.score >= 80
        ? (lang === 'ar' ? 'جيد جداً'        : 'Very Good')
        : qs.score >= 70
          ? (lang === 'ar' ? 'جيد'            : 'Good')
          : qs.score >= 60
            ? (lang === 'ar' ? 'مقبول'        : 'Pass')
            : (lang === 'ar' ? 'يحتاج مراجعة' : 'Needs Work');

    const RECO = {
      comprehension_check: 'أحسنت! فهمك للمفاهيم يتحسن باستمرار 🌱',
      unit_test:           'ممتاز! راجع نتائجك واعرف أين تحتاج تقوية 💪',
      unit:                'ممتاز! راجع نتائجك واعرف أين تحتاج تقوية 💪',
      weekly:              'أسبوع ناجح! حافظ على هذا الإيقاع 🔥',
      monthly:             'نتيجة الشهر محفوظة. اشتغل على نقاط ضعفك 🎯',
      comprehensive:       'مراجعة شاملة ممتازة! جهبذ فخور بيك 🏅',
      mid_year:            'نصف العام اتخطى! الجزء الثاني أسهل معك 💡',
      end_year:            'أنهيت العام! هذه النتيجة ستبقى في سجلك 🏆',
      challenge:           'التحدي انتهى! قارن نتيجتك مع زملائك ⚡',
      on_demand:           'مراجعة مخصصة اكتملت. استمر في التعلم 🎯',
      demand:              'مراجعة مخصصة اكتملت. استمر في التعلم 🎯',
    };
    const RECO_EN = {
      comprehension_check: 'Great job! Your understanding keeps improving 🌱',
      unit_test:           'Excellent! Review your results and find areas to strengthen 💪',
      unit:                'Excellent! Review your results and find areas to strengthen 💪',
      weekly:              'Successful week! Keep this momentum going 🔥',
      monthly:             'Monthly result saved. Work on your weak spots 🎯',
      comprehensive:       'Great comprehensive review! Jahbath is proud of you 🏅',
      mid_year:            'Mid-year done! The second half will be easier 💡',
      end_year:            'Year complete! This result stays in your record 🏆',
      challenge:           'Challenge done! Compare your score with classmates ⚡',
      on_demand:           'Custom review complete. Keep learning 🎯',
      demand:              'Custom review complete. Keep learning 🎯',
    };
    const recoMsg   = RECO[qs.quiz_type]    || 'أحسنت! نتيجتك محفوظة في سجلك 📊';
    const recoMsgEn = RECO_EN[qs.quiz_type] || 'Well done! Your result is saved in your record 📊';

    return (
      <>
      <CanvasPortal />
      <div style={{
        minHeight: '100vh',
        background: 'transparent',
        fontFamily: "'Sora',sans-serif",
        position: 'relative',
      }} dir={qDir}>

        <style>{`
          @keyframes rq-up {
            from { opacity:0; transform:translateY(24px); }
            to   { opacity:1; transform:none; }
          }
          .rq-section { animation: rq-up .55s cubic-bezier(.22,1,.36,1) both; }
          .rq-s1 { animation-delay: .05s; }
          .rq-s2 { animation-delay: .12s; }
          .rq-s3 { animation-delay: .19s; }
          .rq-s4 { animation-delay: .26s; }
          .rq-s5 { animation-delay: .33s; }
          .rq-s6 { animation-delay: .40s; }
          @media (max-width: 640px) {
            .rq-grid-2 { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* ── Inner content container ── */}
        <div style={{
          maxWidth: 900, margin: '0 auto',
          padding: '32px 24px 48px',
          display: 'flex', flexDirection: 'column', gap: 16,
          position: 'relative', zIndex: 10,
        }}>

          {/* ── ROW 1: Ring (left) + XP & Stats (right) ── */}
          <div className="rq-grid-2" style={{
            display: 'grid', gridTemplateColumns: '1.3fr 1fr',
            gap: 16, alignItems: 'start',
          }}>

            {/* Ring card */}
            <div className="rq-section rq-s1" style={{
              background: 'white', borderRadius: 32, padding: '40px 40px 32px',
              border: '1px solid #E5E7EB', borderTop: `4px solid ${meta.color}`,
              boxShadow: '0 8px 32px rgba(5,150,105,.12)',
              textAlign: 'center', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 12, width: '100%', height: '100%',
              boxSizing: 'border-box',
            }}>
              {/* Type badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22 }}>{meta.icon || '📝'}</span>
                <span style={{
                  padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  background: meta.bg, color: meta.color,
                }}>{typeLabel}</span>
              </div>

              {/* Subject name + subtitle */}
              {qs.subject_name && (
                <div style={{ marginTop: -4 }}>
                  <div style={{ fontSize: 13, color: '#6B7280' }}>{qs.subject_name}</div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 500, marginTop: 2 }}>
                    {lang === 'ar' ? 'جهبذ — تعلّم بذكاء' : 'Jahbath — Learn Smart'}
                  </div>
                </div>
              )}

              {/* Score ring */}
              <div style={{ margin: '8px 0 4px' }}>
                <CircleRing pct={qs.score} size={180} strokeWidth={14} animate />
              </div>

              {/* من X سؤال */}
              <div style={{ fontSize: 13, color: '#9CA3AF', fontWeight: 600, marginTop: 6 }}>
                {lang === 'ar' ? `من ${qs.total} سؤال` : `out of ${qs.total} questions`}
              </div>

              {/* Grade label */}
              <div style={{ fontSize: 18, fontWeight: 800, color: gradeColor }}>
                {gradeLabel}
              </div>
            </div>

            {/* Right column: XP card + Stat chips */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

              {/* XP card */}
              <div className="rq-section rq-s3" style={{
                background: `linear-gradient(135deg,${meta.color},${meta.color}CC)`,
                borderRadius: 18, padding: '18px 22px',
                boxShadow: `0 8px 28px ${meta.color}44`,
                display: 'flex', alignItems: 'center', gap: 16,
                position: 'relative', overflow: 'hidden', width: '100%',
              }}>
                <div style={{ position: 'absolute', top: -30, right: -30, width: 120,
                  height: 120, borderRadius: '50%', background: 'rgba(255,255,255,.1)' }} />
                <span style={{ fontSize: 36, flexShrink: 0 }}>⭐</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,.7)',
                    textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 4 }}>
                    {lang === 'ar' ? 'نقاط XP مكتسبة' : 'XP Earned'}
                  </div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, color: '#fff' }}>
                    +{qs.xp_earned}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.65)', marginTop: 3 }}>
                    {lang === 'ar' ? 'أحسنت! استمر في التعلم' : 'Keep it up!'}
                  </div>
                </div>
                <div style={{ flexShrink: 0, width: 100 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                    fontSize: 10, color: 'rgba(255,255,255,.6)', fontWeight: 700, marginBottom: 5 }}>
                    <span>{lang === 'ar' ? 'المستوى' : 'Level'}</span>
                    <span>{Math.min(Math.round((qs.xp_earned / 100) * 100), 100)}%</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,.2)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', background: 'rgba(255,255,255,.75)', borderRadius: 6,
                      width: `${Math.min(Math.round((qs.xp_earned / 100) * 100), 100)}%`,
                      transition: 'width 1.5s cubic-bezier(.34,1.56,.64,1)',
                    }} />
                  </div>
                </div>
              </div>

              {/* Stat chips */}
              <div className="rq-section rq-s2" style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10,
              }}>
                {[
                  { icon: '✅', label: lang === 'ar' ? 'صحيح' : 'Correct', val: qs.correct, color: '#059669', showPct: true  },
                  { icon: '❌', label: lang === 'ar' ? 'خطأ'  : 'Wrong',   val: qs.wrong,  color: '#DC2626', showPct: false },
                  { icon: '⏱',  label: lang === 'ar' ? 'الوقت': 'Time',    val: timeStr,   color: '#1E3A5F', showPct: false },
                ].map(({ icon, label, val, color, showPct }) => (
                  <div key={label} style={{
                    background: 'white', border: '1px solid #E5E7EB', borderRadius: 16,
                    padding: '14px 10px', textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,.05)',
                  }}>
                    <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
                    <div style={{
                      fontFamily: "'Sora',sans-serif", fontSize: 22, fontWeight: 800, color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>
                      {val}
                      {showPct && qs.total > 0 && (
                        <span style={{
                          fontSize: 11, fontWeight: 800, color: '#059669',
                          background: 'rgba(5,150,105,.12)', padding: '2px 7px',
                          borderRadius: 8, marginRight: 4,
                        }}>
                          {Math.round(qs.correct / qs.total * 100)}%
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 10, color: '#9CA3AF', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 4,
                    }}>{label}</div>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {/* ── ROW 2: Recommendation (left) + Answer Review (right) ── */}
          <div className="rq-grid-2" style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: 16, alignItems: 'start',
          }}>

            {/* Recommendation card */}
            <div className="rq-section rq-s4" style={{
              background: 'white', border: '1px solid #E5E7EB',
              borderRadius: 18, padding: 20, width: '100%', height: '100%',
              boxSizing: 'border-box',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 38, height: 38, borderRadius: 12, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: 18,
                  background: `${meta.color}18`, flexShrink: 0,
                }}>🧠</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700, color: '#1E3A5F' }}>
                    {lang === 'ar' ? 'توصية جهبذ' : 'Jahbath Tip'}
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    {lang === 'ar' ? 'بناءً على أدائك' : 'Based on your performance'}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 14, color: '#374151', fontWeight: 600, lineHeight: 1.7, marginBottom: 14 }}>
                {lang === 'ar' ? recoMsg : recoMsgEn}
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => setShowReview(true)}
                  style={{
                    padding: '7px 14px', borderRadius: 100, border: 'none', cursor: 'pointer',
                    fontFamily: "'Nunito',sans-serif", fontSize: 12, fontWeight: 700,
                    background: `${meta.color}15`, color: meta.color,
                    transition: 'all .25s cubic-bezier(.34,1.56,.64,1)',
                  }}>
                  📖 {lang === 'ar' ? 'راجع الأخطاء' : 'Review Mistakes'}
                </button>
                {qs.score < 70 && (
                  <button
                    onClick={() => navigate(qs.return_to || '/student/subjects')}
                    style={{
                      padding: '7px 14px', borderRadius: 100, border: 'none', cursor: 'pointer',
                      fontFamily: "'Nunito',sans-serif", fontSize: 12, fontWeight: 700,
                      background: `${meta.color}15`, color: meta.color,
                      transition: 'all .25s cubic-bezier(.34,1.56,.64,1)',
                    }}>
                    🔄 {lang === 'ar' ? 'أعد الاختبار' : 'Retry Quiz'}
                  </button>
                )}
              </div>
            </div>

            {/* Answer review card — always present */}
            {qs.results && qs.results.length > 0 ? (
              <div className="rq-section rq-s5" style={{
                background: 'white', border: '1px solid #E5E7EB',
                borderRadius: 18, padding: 20, width: '100%', height: '100%',
                boxSizing: 'border-box',
              }}>
                <div
                  onClick={() => setShowReview(v => !v)}
                  style={{ display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', cursor: 'pointer',
                    marginBottom: showReview ? 16 : 0 }}
                >
                  <div style={{ fontFamily: "'Sora',sans-serif", fontSize: 14,
                    fontWeight: 700, color: '#1E3A5F', display: 'flex', alignItems: 'center', gap: 8 }}>
                    📋 {lang === 'ar' ? 'مراجعة الإجابات' : 'Answer Review'}
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF' }}>
                      {qs.results.length} {lang === 'ar' ? 'أسئلة' : 'questions'}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 16, color: '#9CA3AF',
                    transform: showReview ? 'rotate(180deg)' : 'none',
                    transition: '.3s', display: 'inline-block',
                  }}>▼</span>
                </div>
                {showReview && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {qs.results.map((r, i) => {
                      const isCorrect   = r.isCorrect;
                      const borderColor = isCorrect ? '#059669' : '#DC2626';
                      const bgColor     = isCorrect ? 'rgba(209,250,229,.4)' : 'rgba(254,226,226,.4)';
                      return (
                        <div key={i} style={{
                          borderRadius: 12, padding: '12px 14px',
                          borderRight: `3.5px solid ${borderColor}`,
                          background: bgColor,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <span style={{
                              fontFamily: "'Nunito',sans-serif", fontSize: 10, fontWeight: 700,
                              padding: '2px 8px', borderRadius: 100, color: '#fff',
                              background: borderColor,
                            }}>
                              {lang === 'ar' ? `س${i + 1}` : `Q${i + 1}`}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 800, color: borderColor }}>
                              {isCorrect
                                ? (lang === 'ar' ? 'صحيح ✓' : 'Correct ✓')
                                : (lang === 'ar' ? 'خطأ ✗'  : 'Wrong ✗')}
                            </span>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#374151',
                            lineHeight: 1.55, marginBottom: (r.correctAnswer || r.question?.correct_answer) ? 6 : 0 }}>
                            {r.question?.question_text || r.question?.text || r.text || ''}
                          </div>
                          {(r.correctAnswer || r.question?.correct_answer) && (
                            <div style={{ fontSize: 12, color: '#059669', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%',
                                background: '#059669', display: 'inline-block', flexShrink: 0 }} />
                              {lang === 'ar' ? 'الصحيح: ' : 'Correct: '}{r.correctAnswer || r.question.correct_answer}
                            </div>
                          )}
                          {!isCorrect && r.answer && (
                            <div style={{ fontSize: 12, color: '#DC2626', fontWeight: 600,
                              display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%',
                                background: '#DC2626', display: 'inline-block', flexShrink: 0 }} />
                              {lang === 'ar' ? 'إجابتك: ' : 'Your answer: '}{r.answer}
                            </div>
                          )}
                          {r.feedback && (
                            <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 500, marginTop: 4, lineHeight: 1.5, direction: lang === 'ar' ? 'rtl' : 'ltr' }}>
                              {lang === 'ar' ? 'ملاحظة المصحّح: ' : 'Feedback: '}{r.feedback}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="rq-section rq-s5" style={{
                background: 'white', border: '1px solid #E5E7EB',
                borderRadius: 18, padding: 20, width: '100%', height: '100%',
                boxSizing: 'border-box',
              }}>
                <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '24px 0' }}>
                  📭 {lang === 'ar' ? 'لا توجد إجابات للمراجعة' : 'No answers to review'}
                </div>
              </div>
            )}

          </div>

          {/* ── ROW 3: Bottom buttons ── */}
          <div className="rq-section rq-s6" style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <button
                onClick={() => navigate(qs.return_to || '/student/subjects')}
                style={{
                  padding: 14, borderRadius: 16, border: 'none', cursor: 'pointer',
                  fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 800,
                  color: '#fff', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8,
                  background: `linear-gradient(135deg,${meta.color},${meta.color}CC)`,
                  boxShadow: `0 6px 20px ${meta.color}44`,
                  transition: 'all .3s cubic-bezier(.34,1.56,.64,1)',
                }}>
                🚀 {lang === 'ar' ? 'جلسة جديدة' : 'New Session'}
              </button>
              <button
                onClick={() => navigate('/student/dashboard')}
                style={{
                  padding: 14, borderRadius: 16, cursor: 'pointer',
                  fontFamily: "'Sora',sans-serif", fontSize: 13, fontWeight: 800,
                  color: '#374151', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8,
                  background: 'rgba(255,255,255,.85)',
                  backdropFilter: 'blur(16px)',
                  border: '1px solid rgba(255,255,255,.9)',
                  boxShadow: '0 4px 16px rgba(0,0,0,.06)',
                  transition: 'all .3s cubic-bezier(.34,1.56,.64,1)',
                }}>
                📊 {lang === 'ar' ? 'الرئيسية' : 'Dashboard'}
              </button>
            </div>
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <button
                onClick={() => setQuizDismissed(true)}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: '#9CA3AF', fontSize: 12, textDecoration: 'underline', marginTop: 4,
                  fontFamily: "'Sora',sans-serif",
                }}>
                {lang === 'ar' ? 'عرض السجل الكامل' : 'View Full History'}
              </button>
            </div>
          </div>

        </div>
      </div>
      </>
    );
  }

  return (
    <>
    <CanvasPortal />
    <div style={{
      minHeight: '100vh',
      background: 'transparent',
      padding: 24, paddingTop: 12, fontFamily: "'Sora',sans-serif",
      position: 'relative',
    }}>
      <div style={{ position: 'relative', zIndex: 10 }}>
      <style>{`
        @keyframes sd-shimmer { from{left:-100%} to{left:200%} }
        .rg-shim {
          background: linear-gradient(90deg,#F3F4F6 25%,#E5E7EB 50%,#F3F4F6 75%);
          background-size: 200% 100%;
          animation: sd-shimmer 1.5s infinite;
        }
        .rg-card {
          cursor: pointer; padding: 12px 10px; height: 100%; box-sizing: border-box;
          box-shadow: 0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07);
          transition: transform .15s, box-shadow .15s, border-color .15s;
        }
        .rg-card:hover:not(.rg-selected) {
          transform: translateY(-6px);
          box-shadow: 0 5px 0 #D1D5DB, 0 18px 36px rgba(0,0,0,.1);
          border-color: #D1D5DB;
        }
        .rg-card.rg-selected {
          box-shadow: 0 5px 0 #059669, 0 18px 36px rgba(5,150,105,.18);
          border-color: #059669;
          transform: translateY(-3px);
        }
        .rg-card:active {
          transform: translateY(3px);
          box-shadow: 0 1px 0 #E5E7EB;
        }
        .rg-card:hover .rg-bar {
          transform: scaleY(1.3);
          transform-origin: bottom;
          transition: transform .2s ease;
        }
        .rg-grid { display: grid; gap: 14px; grid-template-columns: repeat(2,1fr); height: calc(100vh - 120px); grid-template-rows: repeat(2, 1fr); overflow: hidden; padding-top: 12px; }
        @media (min-width: 480px)  { .rg-grid { grid-template-columns: repeat(3,1fr); } }
        @media (min-width: 768px)  { .rg-grid { grid-template-columns: repeat(4,1fr); } }
        @media (min-width: 1024px) { .rg-grid { grid-template-columns: repeat(5,1fr); } }
        @keyframes slideDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:translateY(0)} }
        .rg-close-btn:hover  { border-color: #059669 !important; color: #059669 !important; }
        .rg-session-dot:hover .rg-dot-circle { transform: scale(1.2); }
        .rg-session-dot:hover .rg-dot-date   { color: #059669; }
        .rg-exam-row:hover   { background: #F9FFF9; }
        @keyframes popUp { from{opacity:0;transform:scale(0.94) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes spin   { to { transform: rotate(360deg) } }
        .rg-modal-close:hover { background: #FEF2F2 !important; border-color: #FCA5A5 !important; color: #EF4444 !important; }
      `}</style>

      <StudentHeader
        title={selectedSubjectId
          ? (subjects.find(s => s.subject_id === selectedSubjectId)?.subject_name || '')
          : (globalLang === 'ar' ? 'نتائجي' : 'My Results')}
        showBack
        onBack={() => selectedSubjectId ? setSelectedSubjectId(null) : window.history.back()}
        lang={globalLang}
      />

      {/* Loading: 6 skeleton cards */}
      {loading && (
        <div className="rg-grid">
          {[0, 1, 2, 3, 4, 5].map(i => <SkeletonCard key={i} />)}
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '60px 20px', gap: 16, textAlign: 'center',
        }}>
          <p style={{ color: '#DC2626', fontSize: 15, margin: 0 }}>{error}</p>
          <button
            onClick={load}
            style={{
              background: '#059669', color: 'white', border: 'none',
              padding: '10px 28px', borderRadius: 20, cursor: 'pointer',
              fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            {t(globalLang, 'retry')}
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && subjects.length === 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '80px 20px', textAlign: 'center', gap: 16,
        }}>
          <span style={{ fontSize: 56 }}>🚀</span>
          <p style={{ fontSize: 17, color: '#6B7280', margin: 0 }}>
            {t(globalLang, 'empty')}
          </p>
        </div>
      )}

      {/* Subject grid */}
      <div style={{ display: selectedSubjectId ? 'none' : 'block' }}>
        {!loading && !error && subjects.length > 0 && (
          <div className="rg-grid">
            {subjects.map(subj => (
              <SubjectCard
                key={subj.subject_id}
                subj={subj}
                selected={selectedSubjectId === subj.subject_id}
                onClick={() => { setSelectedSubjectId(subj.subject_id); setActiveTab('exams'); }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Detail Panel ── */}
      <div style={{ display: selectedSubjectId ? 'block' : 'none' }}>
      {selectedSubjectId !== null && (() => {
        const subject = subjects.find(s => s.subject_id === selectedSubjectId);
        if (!subject) return null;
        const lang         = subjLang(subject);
        const dir          = lang === 'ar' ? 'rtl' : 'ltr';
        const spct         = Math.round(subject.avg_correct_percentage || 0);
        const currentLevel = subject.sessions?.[0]?.level_at_end || 'Medium';
        const sessions     = subject.sessions    || [];
        const exams        = subject.exams       || [];
        const improvements = subject.weaknesses  || [];

        const encourageMsg = spct >= 70
          ? (lang === 'ar' ? 'أداء رائع! استمر 🌟'    : "You're doing amazing! Keep it up 🌟")
          : spct >= 40
            ? (lang === 'ar' ? 'أنت تتحسن! واصل 💪'  : "You're improving! Keep pushing 💪")
            : (lang === 'ar' ? 'كل جلسة تقوّيك 🚀'    : 'Every session makes you stronger 🚀');

        const tiles = [
          { icon: '📚', val: subject.sessions_count || 0, label: t(lang, 'sessions'), bg: '#ECFDF5', color: '#059669'  },
          { icon: '📝', val: exams.length,                label: t(lang, 'exams'),    bg: '#FFFBEB', color: '#D97706'  },
          { icon: '🎯', val: improvements.length,         label: t(lang, 'toImprove'),bg: '#F5F3FF', color: '#7C3AED'  },
          { icon: '⭐', val: subject.total_xp || 0,       label: t(lang, 'xp'),       bg: '#EFF6FF', color: '#1E40AF'  },
        ];

        return (
          <div
            key={selectedSubjectId}
            ref={detailRef}
            dir={dir}
            style={{
              background: 'white', borderRadius: 28, border: '2px solid #E5E7EB',
              overflow: 'hidden', animation: 'slideDown 0.3s ease',
              boxShadow: '0 5px 0 #E5E7EB, 0 12px 32px rgba(0,0,0,.07)',
              fontFamily: "'Sora',sans-serif",
            }}
          >

            {/* ── Stat tiles ── */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4,1fr)',
              gap: 10, padding: '16px 22px', borderBottom: '1px solid #F3F4F6',
            }}>
              {tiles.map((tile, i) => (
                <div key={i} style={{
                  borderRadius: 18, padding: '12px 10px', textAlign: 'center',
                  background: tile.bg, border: '2px solid #E5E7EB',
                  boxShadow: '0 3px 0 #E5E7EB',
                }}>
                  <span style={{ fontSize: 18, display: 'block', marginBottom: 5 }}>{tile.icon}</span>
                  <span style={{ fontSize: 20, fontWeight: 800, display: 'block', lineHeight: 1, color: tile.color }}>
                    {tile.val}
                  </span>
                  <span style={{
                    fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.4px', opacity: 0.7, marginTop: 3, display: 'block',
                    color: tile.color,
                  }}>
                    {tile.label}
                  </span>
                </div>
              ))}
            </div>

            {/* ── Session timeline ── */}
            <div style={{ padding: '16px 22px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 12,
              }}>
                {t(lang, 'recentSessions')}
              </div>

              {sessions.length === 0
                ? <div style={{ fontSize: 12, color: '#9CA3AF', padding: '8px 0' }}>{t(lang, 'noData')}</div>
                : (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0 }}>
                    {sessions.slice(0, 5).map((s, i) => {
                      const sp  = Math.round(s.avg_correct_percentage || 0);
                      const dc  = ringColor(sp).stroke;
                      const sName = (s.unit_name || '');
                      const sShort = sName.length > 8 ? sName.slice(0, 8) + '…' : sName;
                      return (
                        <React.Fragment key={i}>
                          <div
                            className="rg-session-dot"
                            onClick={() => openSessionModal(s.session_id)}
                            style={{
                              flex: 1, display: 'flex', flexDirection: 'column',
                              alignItems: 'center', gap: 5, position: 'relative', cursor: 'pointer',
                            }}
                          >
                            {s.level_changed && (
                              <span style={{
                                position: 'absolute', top: -14, fontSize: 12,
                                fontWeight: 800, color: '#059669',
                              }}>↑</span>
                            )}
                            <div
                              className="rg-dot-circle"
                              style={{
                                width: 36, height: 36, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 10, fontWeight: 800, color: 'white',
                                background: dc, transition: 'transform 0.2s',
                              }}
                            >
                              {sp}%
                            </div>
                            <div className="rg-dot-date" style={{ fontSize: 9, color: '#9CA3AF', transition: 'color .2s' }}>
                              {fmtDate(s.created_at)}
                            </div>
                            <div style={{ fontSize: 8, color: '#6B7280', textAlign: 'center', maxWidth: 50 }}>
                              {sShort}
                            </div>
                            <div style={{ fontSize: 8, color: '#10B981', fontWeight: 700 }}>↗ view</div>
                          </div>
                          {i < Math.min(sessions.length, 5) - 1 && (
                            <div style={{
                              height: 2, background: '#E5E7EB', flex: 1,
                              alignSelf: 'center', marginBottom: 26,
                            }} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )
              }
            </div>

            {/* ── Tabs ── */}
            <div style={{ padding: '16px 22px 0' }}>
              <div style={{
                display: 'flex', borderBottom: '2px solid #F3F4F6', marginBottom: 14,
              }}>
                {[
                  { key: 'exams',   label: t(lang, 'examsTab')   },
                  { key: 'improve', label: t(lang, 'improveTab') },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      fontSize: 12, fontWeight: 700, padding: '7px 16px',
                      border: 'none', background: 'none', cursor: 'pointer',
                      fontFamily: "'Sora',sans-serif", transition: 'all 0.2s',
                      color:        activeTab === tab.key ? '#059669'           : '#9CA3AF',
                      borderBottom: activeTab === tab.key ? '2px solid #059669' : '2px solid transparent',
                      marginBottom: -2,
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Exams tab */}
              {activeTab === 'exams' && (
                <div style={{ paddingBottom: 16 }}>
                  {exams.length === 0
                    ? <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '16px 0', fontSize: 12 }}>
                        {t(lang, 'noExams')}
                      </div>
                    : exams.map((ex, i) => {
                        const m  = examMeta(ex.exam_type);
                        const ep = Math.round(ex.correct_percentage || 0);
                        return (
                          <div
                            key={ex.exam_id || i}
                            className="rg-exam-row"
                            onClick={() => openExamModal(ex.exam_id)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '9px 4px', borderBottom: '1px solid #F9FAFB',
                              cursor: 'pointer', borderRadius: 8,
                              transition: 'background 0.15s',
                            }}
                          >
                            <span style={{
                              padding: '3px 9px', borderRadius: 20, fontSize: 10,
                              fontWeight: 700, background: m.bg, color: m.color, flexShrink: 0,
                            }}>
                              {m.label}
                            </span>
                            <span style={{ fontWeight: 800, color: '#1E3A5F', fontSize: 12 }}>
                              {ex.total_score}/{ex.total_max_score}
                            </span>
                            <span style={{ fontWeight: 800, fontSize: 12, color: ringColor(ep).text }}>
                              {ep}%
                            </span>
                            <span style={{ color: '#9CA3AF', fontSize: 11, marginInlineStart: 'auto' }}>
                              {fmtDate(ex.created_at)}
                            </span>
                            <span style={{ color: '#10B981', fontSize: 11, fontWeight: 700 }}>↗</span>
                          </div>
                        );
                      })
                  }
                </div>
              )}

              {/* Areas to Improve tab */}
              {activeTab === 'improve' && (
                <div style={{ paddingBottom: 16 }}>
                  {improvements.length === 0
                    ? <div style={{
                        textAlign: 'center', color: '#059669', fontWeight: 700,
                        padding: '16px 0', fontSize: 12,
                      }}>
                        {t(lang, 'noImprove')}
                      </div>
                    : improvements.map((w, i) => {
                        const isConcept = w.weakness_type === 'conceptual';
                        const filled    = Math.min(w.occurrence_count || 0, 3);
                        return (
                          <div key={i} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '9px 0', borderBottom: '1px solid #F9FAFB',
                          }}>
                            <div style={{
                              width: 8, height: 8, borderRadius: '50%',
                              background: '#F59E0B', flexShrink: 0,
                            }} />
                            <span style={{
                              padding: '3px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                              background: isConcept ? '#F5F3FF' : '#FEF3C7',
                              color:      isConcept ? '#7C3AED' : '#D97706',
                              flexShrink: 0,
                            }}>
                              {isConcept ? (lang === 'ar' ? 'مفاهيمي' : 'Concept') : (lang === 'ar' ? 'تطبيقي' : 'Application')}
                            </span>
                            <span style={{ fontSize: 12, color: '#374151', flex: 1, lineHeight: 1.4 }}>
                              {w.learning_objective_id}
                            </span>
                            <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                              {Array.from({ length: 3 }, (_, j) => (
                                <div key={j} style={{
                                  width: 7, height: 7, borderRadius: '50%',
                                  background: j < filled ? '#7C3AED' : '#E5E7EB',
                                }} />
                              ))}
                            </div>
                          </div>
                        );
                      })
                  }
                </div>
              )}
            </div>

            {/* ── Avatar section ── */}
            <div style={{
              padding: '14px 22px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1E3A5F' }}>
                  {encourageMsg}
                </div>
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>
                  {lang === 'ar' ? 'آخر جلسة: ' : 'Last session: '}{fmtDate(subject.last_session_date)}
                </div>
              </div>
              {avatarUrl && (
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <img
                    src={avatarUrl}
                    alt="avatar"
                    style={{
                      height: 200,
                      objectFit: 'contain',
                      mixBlendMode: 'multiply',
                      filter: 'drop-shadow(0 4px 12px rgba(5,150,105,0.15))',
                      background: 'transparent',
                    }}
                  />
                </div>
              )}
            </div>

          </div>
        );
      })()}

      {/* ── Modal Overlay ── */}
      {(sessionModal || examModal || modalLoading) && (
        <div
          onClick={() => { setSessionModal(null); setExamModal(null); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,30,20,0.35)',
            backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 200, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: 28, width: '100%', maxWidth: 520,
              maxHeight: '85vh', overflowY: 'auto',
              border: '2px solid #E5E7EB',
              boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
              animation: 'popUp 0.25s ease', fontFamily: "'Sora',sans-serif",
            }}
          >

            {/* Spinner */}
            {modalLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  border: '3px solid #E5E7EB', borderTopColor: '#059669',
                  animation: 'spin 0.8s linear infinite',
                }} />
              </div>
            )}

            {/* Error */}
            {!modalLoading && modalError && (
              <div style={{ color: '#EF4444', fontSize: 13, padding: 24, textAlign: 'center' }}>
                {modalError}
              </div>
            )}

            {/* ── Session modal ── */}
            {!modalLoading && !modalError && sessionModal && sessionModal !== 'loading' && (() => {
              const selSubj = subjects.find(s => s.subject_id === selectedSubjectId);
              const lang    = selSubj ? subjLang(selSubj) : 'en';
              const sm      = sessionModal;
              const spct    = Math.round(sm.avg_correct_percentage || 0);
              const allTopics = [...new Set([...(sm.topics_covered || []), ...(sm.concepts_covered || [])])];

              return (
                <>
                  {/* Header */}
                  <div style={{
                    padding: '20px 22px 16px', borderBottom: '1px solid #F3F4F6',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#1E3A5F' }}>
                        {t(lang, 'sessionModalTitle')} — {sm.unit_name}
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                        {fmtDate(sm.created_at)}{sm.duration_minutes ? ` · ${sm.duration_minutes} min` : ''}
                      </div>
                    </div>
                    <button
                      className="rg-modal-close"
                      onClick={() => setSessionModal(null)}
                      style={{
                        width: 28, height: 28, border: '1.5px solid #E5E7EB',
                        background: 'white', cursor: 'pointer', fontSize: 14,
                        color: '#9CA3AF', borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .2s', fontFamily: 'inherit',
                      }}
                    >×</button>
                  </div>

                  {/* Score band */}
                  <div style={{
                    margin: '16px 22px', borderRadius: 16, padding: 16,
                    background: 'linear-gradient(135deg,#F0FDF4,#ECFDF5)',
                    display: 'flex', gap: 16, alignItems: 'center',
                  }}>
                    <CircleRing pct={spct} size={70} strokeWidth={6} />
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#1E3A5F' }}>
                        {sm.graded_via_modal
                          ? `${sm.total_score}%`
                          : `${sm.total_score} / ${sm.total_max_score} pts`}
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                        {sm.graded_via_modal
                          ? (lang === 'ar' ? 'تم التقييم عبر الاختبار' : 'Graded via quiz')
                          : `${sm.correct_count} ${t(lang, 'correct')} · ${sm.wrong_count} ${t(lang, 'wrong')}`}
                      </div>
                      <div style={{
                        display: 'inline-flex', alignItems: 'center',
                        background: '#F0FDF4', border: '1px solid #10B981',
                        borderRadius: 20, padding: '4px 10px',
                        fontSize: 10, fontWeight: 700, color: '#059669', marginTop: 6,
                      }}>
                        {sm.level_changed
                          ? `📈 ${sm.level_at_start} → ${sm.level_at_end}`
                          : `📊 ${sm.level_at_end} level`}
                      </div>
                    </div>
                  </div>

                  {/* Topics covered */}
                  <div style={{ padding: '0 22px 14px' }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
                    }}>
                      {t(lang, 'topicsCovered')}
                    </div>
                    <div>
                      {allTopics.map((tp, i) => (
                        <span key={i} style={{
                          display: 'inline-block', background: '#F0FDF4',
                          border: '1px solid #D1FAE5', borderRadius: 20,
                          padding: '4px 10px', fontSize: 11, fontWeight: 600,
                          color: '#059669', margin: 3,
                        }}>{tp}</span>
                      ))}
                    </div>
                  </div>

                  {/* Strengths */}
                  {(sm.strengths || []).length > 0 && (
                    <div style={{ padding: '0 22px 14px' }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
                      }}>
                        {t(lang, 'strengths')}
                      </div>
                      {sm.strengths.map((s, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 0', borderBottom: '1px solid #F9FAFB',
                        }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: '#374151' }}>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Areas to work on */}
                  {(sm.improvement_areas || []).length > 0 && (
                    <div style={{ padding: '0 22px 14px' }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
                      }}>
                        {t(lang, 'areasToWork')}
                      </div>
                      {sm.improvement_areas.map((a, i) => (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '7px 0', borderBottom: '1px solid #F9FAFB',
                        }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B', flexShrink: 0 }} />
                          <span style={{ fontSize: 12, color: '#374151' }}>{a}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Question breakdown */}
                  <div style={{ padding: '0 22px 20px' }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
                    }}>
                      {t(lang, 'questionBreakdown')}
                    </div>
                    {(sm.questions || []).map((q, i) => {
                      const icon  = q.is_correct === true ? '✅' : q.is_correct === false ? '❌' : '⏭';
                      const qText = (q.question_text || '').length > 80
                        ? q.question_text.slice(0, 80) + '...' : (q.question_text || '');
                      const badge = q.is_correct === true
                        ? { bg: '#ECFDF5', color: '#059669', label: t(lang, 'correct') }
                        : q.is_correct === false
                          ? { bg: '#FEF2F2', color: '#EF4444', label: t(lang, 'wrong') }
                          : { bg: '#F3F4F6', color: '#9CA3AF', label: '—' };
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 0', borderBottom: '1px solid #F9FAFB',
                        }}>
                          <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                          <span style={{ flex: 1, fontSize: 11, color: '#374151', lineHeight: 1.4 }}>{qText}</span>
                          <span style={{
                            fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                            background: badge.bg, color: badge.color, flexShrink: 0,
                          }}>{badge.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            {/* ── Exam modal ── */}
            {!modalLoading && !modalError && examModal && examModal !== 'loading' && (() => {
              const selSubj = subjects.find(s => s.subject_id === selectedSubjectId);
              const lang    = selSubj ? subjLang(selSubj) : 'en';
              const em      = examModal;
              const epct    = Math.round(em.correct_percentage || 0);
              const m       = examMeta(em.exam_type);

              return (
                <>
                  {/* Header */}
                  <div style={{
                    padding: '20px 22px 16px', borderBottom: '1px solid #F3F4F6',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                  }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#1E3A5F' }}>
                        {t(lang, 'examModalTitle')} — {m.label}
                      </div>
                      <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                        {fmtDate(em.created_at)}
                      </div>
                    </div>
                    <button
                      className="rg-modal-close"
                      onClick={() => setExamModal(null)}
                      style={{
                        width: 28, height: 28, border: '1.5px solid #E5E7EB',
                        background: 'white', cursor: 'pointer', fontSize: 14,
                        color: '#9CA3AF', borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all .2s', fontFamily: 'inherit',
                      }}
                    >×</button>
                  </div>

                  {/* Score band */}
                  <div style={{
                    margin: '16px 22px', borderRadius: 16, padding: 16,
                    background: 'linear-gradient(135deg,#EFF6FF,#F0F9FF)',
                    display: 'flex', gap: 16, alignItems: 'center',
                  }}>
                    <CircleRing pct={epct} size={70} strokeWidth={6} />
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#1E3A5F' }}>
                        {em.total_score} / {em.total_max_score} pts
                      </div>
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                        {em.correct_count} {lang === 'ar' ? 'صحيح' : 'correct'} · {em.wrong_count} {lang === 'ar' ? 'خطأ' : 'wrong'}
                      </div>
                      <span style={{
                        display: 'inline-block', padding: '3px 9px', borderRadius: 20,
                        fontSize: 10, fontWeight: 700, background: m.bg, color: m.color, marginTop: 6,
                      }}>
                        {m.label}
                      </span>
                    </div>
                  </div>

                  {/* Topic breakdown */}
                  {Object.keys(em.topic_breakdown || {}).length > 0 && (
                    <div style={{ padding: '0 22px 16px' }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10,
                      }}>
                        {t(lang, 'topicBreakdown')}
                      </div>
                      {Object.entries(em.topic_breakdown).map(([key, val], i) => {
                        const tp = Math.round(val.percentage || 0);
                        return (
                          <div key={i}>
                            <div style={{
                              display: 'flex', justifyContent: 'space-between',
                              fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 4,
                            }}>
                              <span>{key}</span>
                              <span style={{ color: ringColor(tp).text }}>{tp}%</span>
                            </div>
                            <div style={{
                              height: 8, background: '#F3F4F6', borderRadius: 10,
                              overflow: 'hidden', marginBottom: 10,
                              boxShadow: 'inset 0 1px 2px rgba(0,0,0,.08)',
                            }}>
                              <div style={{
                                height: '100%', width: `${tp}%`,
                                background: ringColor(tp).stroke, borderRadius: 10,
                                position: 'relative', overflow: 'hidden',
                              }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Recommendations */}
                  {em.recommendations && (
                    <div style={{ padding: '0 22px 20px' }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
                      }}>
                        {t(lang, 'recommendations')}
                      </div>
                      <div style={{
                        background: '#F0FDF4', borderRadius: 14, padding: 14,
                        fontSize: 12, color: '#374151', lineHeight: 1.7,
                        border: '1px solid #D1FAE5',
                      }}>
                        {typeof em.recommendations === 'string'
                          ? em.recommendations
                          : <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Nunito', margin: 0, fontSize: 12 }}>
                              {JSON.stringify(em.recommendations, null, 2)}
                            </pre>}
                      </div>
                    </div>
                  )}

                  {/* Question breakdown */}
                  <div style={{ padding: '0 22px 20px' }}>
                    <div style={{
                      fontSize: 10, fontWeight: 700, color: '#9CA3AF',
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8,
                    }}>
                      {t(lang, 'questionBreakdown')}
                    </div>
                    {(em.questions || []).map((q, i) => {
                      const icon  = q.is_correct === true ? '✅' : q.is_correct === false ? '❌' : '⏭';
                      const qText = (q.question_text || '').length > 80
                        ? q.question_text.slice(0, 80) + '...' : (q.question_text || '');
                      const badge = q.is_correct === true
                        ? { bg: '#ECFDF5', color: '#059669', label: t(lang, 'correct') }
                        : q.is_correct === false
                          ? { bg: '#FEF2F2', color: '#EF4444', label: t(lang, 'wrong') }
                          : { bg: '#F3F4F6', color: '#9CA3AF', label: '—' };
                      return (
                        <div key={i} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 0', borderBottom: '1px solid #F9FAFB',
                        }}>
                          <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                          <span style={{ flex: 1, fontSize: 11, color: '#374151', lineHeight: 1.4 }}>{qText}</span>
                          <span style={{
                            fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                            background: badge.bg, color: badge.color, flexShrink: 0,
                          }}>{badge.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

          </div>
        </div>
      )}
      </div>

      </div>
    </div>
    </>
  );
}
